import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.querySelectorAll('form fieldset').forEach((fs) => this.#formatFormFields(fs));
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Wraps label+input pairs inside a fieldset into .rte-field groups,
   * and groups radio/checkbox option labels into .rte-radio-group containers.
   * This enables CSS grid layout with each field group as a single grid item.
   * @param {HTMLFieldSetElement} fieldset
   */
  #formatFormFields(fieldset) {
    const children = Array.from(fieldset.childNodes);
    const fragment = document.createDocumentFragment();
    let i = 0;

    while (i < children.length) {
      const node = children[i];

      if (node.nodeType !== Node.ELEMENT_NODE) {
        fragment.appendChild(node);
        i++;
        continue;
      }

      const tag = node.tagName;

      // Skip hidden inputs — move them as-is
      if (tag === 'INPUT' && (node.type === 'hidden' || (node.type === 'checkbox' && node.tabIndex === -1))) {
        fragment.appendChild(node);
        i++;
        continue;
      }

      // A label followed by an input/select/textarea: wrap into .rte-field
      if (tag === 'LABEL' && !node.querySelector('input, select, textarea')) {
        const next = children[i + 1];
        if (next && next.nodeType === Node.ELEMENT_NODE) {
          const nextTag = next.tagName;

          // label + text input / select / textarea
          if (nextTag === 'INPUT' && !['checkbox', 'radio', 'hidden'].includes(next.type) || nextTag === 'SELECT' || nextTag === 'TEXTAREA') {
            const wrapper = document.createElement('div');
            wrapper.classList.add('rte-field');
            wrapper.appendChild(node);
            wrapper.appendChild(next);
            fragment.appendChild(wrapper);
            i += 2;
            continue;
          }

          // label + file-upload label (label text, then label wrapping <input type="file">)
          if (nextTag === 'LABEL' && next.querySelector('input[type="file"]')) {
            const wrapper = document.createElement('div');
            wrapper.classList.add('rte-field');
            wrapper.appendChild(node);
            wrapper.appendChild(next);
            fragment.appendChild(wrapper);
            i += 2;
            continue;
          }

          // Question label followed by radio/checkbox option labels
          if (nextTag === 'LABEL' && next.querySelector('input[type="radio"], input[type="checkbox"]')) {
            const group = document.createElement('div');
            group.classList.add('rte-field', 'rte-field--radio');
            group.appendChild(node);

            // Gather all consecutive radio/checkbox labels
            let j = i + 1;
            while (j < children.length) {
              const opt = children[j];
              if (opt.nodeType === Node.ELEMENT_NODE && opt.tagName === 'LABEL' && opt.querySelector('input[type="radio"], input[type="checkbox"]')) {
                group.appendChild(opt);
                j++;
              } else {
                break;
              }
            }
            fragment.appendChild(group);
            i = j;
            continue;
          }
        }
      }

      // A label wrapping an input (like file upload labels with no preceding text label)
      if (tag === 'LABEL' && node.querySelector('input[type="file"]')) {
        const wrapper = document.createElement('div');
        wrapper.classList.add('rte-field');
        wrapper.appendChild(node);
        fragment.appendChild(wrapper);
        i++;
        continue;
      }

      // Everything else (h3, hr, p, script, etc.) passes through
      fragment.appendChild(node);
      i++;
    }

    fieldset.appendChild(fragment);
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
